import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetcher,
	useLoaderData,
	useLocation,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { HoneypotInputs, HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ErrorList, Field } from './components/forms.tsx'
import { Progress } from './components/progress-bar.tsx'
import { SearchBar } from './components/search-bar.tsx'
import { SiteLogo } from './components/site-logo.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { Toaster } from './components/ui/react-toastify.tsx'
import { StatusButton } from './components/ui/status-button.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './components/ui/tooltip.tsx'
import { type action } from './routes/_marketing+/newsletter.tsx'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import { EVERY_USER_MENU_ITEMS } from './routes/users+/$username.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { SITE_FULL_TITLE } from './utils/client-constants.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import {
	combineHeaders,
	getDomainUrl,
	getUserImgSrc,
	useIsPending,
} from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { EmailSchema } from './utils/user-validation.ts'
import { useOptionalUser, useUser } from './utils/user.ts'

import 'react-toastify/dist/ReactToastify.css'

export const NewsletterFormSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string(),
})

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon.svg',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{
			rel: 'icon',
			type: 'image/svg+xml',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{
			title: data ? `${SITE_FULL_TITLE}` : `Error | ${SITE_FULL_TITLE}`,
		},
		{ name: 'description', content: `Truly the Best Products in Seconds` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	useEffect(() => {
		if (env.MODE === 'production' && !env.CI) {
			const script = document.createElement('script')
			script.src =
				'https://app.termly.io/resource-blocker/eeddf6cf-ddaa-41b0-bdd5-4581f6434cdc?autoBlock=on'
			script.type = 'text/javascript'
			script.nonce = nonce
			script.async = true
			document.body.appendChild(script)

			return () => {
				document.body.removeChild(script)
			}
		}
	}, [nonce, env])

	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}

				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}

				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	useToast(data.toast)
	const currentPath = useLocation().pathname
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	const homePage = currentPath === '/'
	const searchBar = (
		<SearchBar status="idle" autoSubmit={homePage ? false : true} />
	)
	const footerExcludedPaths = [
		'add-product',
		'login',
		'signup',
		'search',
		'verify',
		'onboarding',
	]
	const showFooter = !footerExcludedPaths.some(path =>
		currentPath.includes(path),
	)
	const params = new URLSearchParams({ redirectTo: currentPath })
	const loginRedirectUrl = homePage ? '/login' : `/login?${params?.toString()}`
	const signUpRedirectUrl = homePage
		? '/signup'
		: `/signup?${params?.toString()}`
	const fetcher = useFetcher<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'newsletter-form',
		constraint: getZodConstraint(NewsletterFormSchema),
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: NewsletterFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<Document
			nonce={nonce}
			theme={'light'} //? if you want to support dark theme, change 'light' to theme
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex min-h-screen w-screen flex-col justify-between overflow-hidden">
				<div>
					<header className="mx-0 px-6 py-6 max-sm:px-2">
						<nav className="flex flex-wrap items-center justify-between gap-3 max-sm:gap-2 sm:flex-nowrap md:gap-8">
							{!homePage ? <Logo /> : <></>}
							<div
								className={`ml-auto flex flex-wrap items-center justify-between gap-2 max-sm:gap-1`}
							>
								<div
									className={`ml-auto ${!homePage ? 'max-sm:hidden' : ''} w-64 max-w-sm flex-1 sm:block`}
								>
									{searchBar}
								</div>
								{!homePage && (
									<div className="flex gap-2">
										<TooltipProvider
											delayDuration={300}
											disableHoverableContent={true}
										>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button asChild>
														<Link
															to="/rankings"
															prefetch="intent"
															title="Rankings"
															onClick={e => {
																if (location.pathname === '/rankings') {
																	e.preventDefault()
																	toast.info(
																		'You are already on the rankings page',
																	)
																}
															}}
														>
															<Icon name="laptop" size="md" />
														</Link>
													</Button>
												</TooltipTrigger>
												<TooltipContent>Rankings</TooltipContent>
											</Tooltip>
										</TooltipProvider>
										<TooltipProvider
											delayDuration={300}
											disableHoverableContent={true}
										>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button asChild variant={'secondary'}>
														<Link
															to="/products/add-product"
															prefetch="intent"
															title="Add product"
															onClick={e => {
																if (
																	location.pathname === '/products/add-product'
																) {
																	e.preventDefault()
																	toast.info(
																		'You are already on the add product page',
																	)
																}
															}}
														>
															<Icon name="plus" size="md" />
														</Link>
													</Button>
												</TooltipTrigger>
												<TooltipContent>Add Product</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									</div>
								)}
								<div className="flex items-center">
									{user ? (
										<UserDropdown />
									) : (
										<div className="flex gap-1">
											<Button asChild variant="link" size={'md'}>
												<Link to={loginRedirectUrl} className="max-sm:text-xs">
													Log In
												</Link>
											</Button>
											<Button asChild variant="default" size={'md'}>
												<Link to={signUpRedirectUrl} className="max-sm:text-xs">
													Sign Up
												</Link>
											</Button>
										</div>
									)}
								</div>
							</div>
							<div
								className={`block w-full sm:hidden ${homePage ? 'max-sm:hidden' : ''}`}
							>
								{searchBar}
							</div>
						</nav>
					</header>
					<div className="mx-auto flex w-full max-w-4xl flex-col">
						<Outlet />
					</div>
					{showFooter && (
						<div className="mx-auto flex w-full max-w-4xl flex-col pt-32">
							<div className="flex justify-between gap-8 max-sm:flex-col max-sm:gap-2 sm:flex-row-reverse">
								<fetcher.Form
									method="POST"
									className="flex flex-col gap-2 rounded-md p-4 drop-shadow max-sm:pl-3 max-sm:pr-3"
									action="/newsletter"
									preventScrollReset={true}
									{...getFormProps(form)}
								>
									<HoneypotInputs />
									<input type="hidden" name="redirectTo" value={currentPath} />
									<Field
										labelProps={{
											htmlFor: fields.email.id,
											children: 'Join our newsletter: Best products & reviews',
											className: 'px-2 text-xs',
										}}
										inputProps={{
											...getInputProps(fields.email, { type: 'email' }),
											placeholder: 'Enter your email...',
											className: 'w-full mt-1.5 text-gray-500',
											autoComplete: 'email',
										}}
										errors={fields.email.errors}
									/>
									<ErrorList errors={form.errors} id={form.errorId} />
									<StatusButton
										status={isPending ? 'pending' : (form.status ?? 'idle')}
										type="submit"
										disabled={isPending}
										variant={'secondary'}
									>
										<Icon name="envelope-closed">Subscribe</Icon>
									</StatusButton>
								</fetcher.Form>
								<div className="-ml-1 pb-4 pr-8 pt-8 max-sm:ml-3">
									<div>
										<ul className="flex gap-2">
											<li>
												<Button size={'xsm'} asChild variant={'link'}>
													<a
														href="https://www.linkedin.com/company/inra-independent-ranking"
														aria-label="Linkedin"
														target="_blank"
														rel="noreferrer"
													>
														<Icon name="linkedin-logo" size="lg" />
													</a>
												</Button>
											</li>
										</ul>
									</div>
									<div className="flex flex-col items-start">
										<Button variant={'link'} asChild size={'xsm'}>
											<Link to={'/contact'}>Contact</Link>
										</Button>
										<Button variant={'link'} asChild size={'xsm'}>
											<Link to={'/privacy-policy'}>Privacy Policy</Link>
										</Button>
										<Button variant={'link'} asChild size={'xsm'}>
											<Link to={'/terms-of-service'}>Terms of Service</Link>
										</Button>
										<Button variant={'link'} asChild size={'xsm'}>
											<Link to={'#'} className="termly-display-preferences">
												Consent Preferences
											</Link>
										</Button>
									</div>
								</div>
							</div>
							<div className="text-fore flex w-full justify-center py-8">
								Independent Ranking
							</div>
						</div>
					)}
				</div>
			</div>
			<Toaster closeButton position="bottom-left" theme={theme} />
			<Progress />
		</Document>
	)
}

function Logo() {
	return (
		<Link to="/">
			<SiteLogo
				showTitle={false}
				titleSize="text-4xl max-sm:text-3xl max-sm:pl-2"
			/>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary" size={'sm'}>
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={e => e.preventDefault()}
						className="flex items-center gap-1"
					>
						<img
							className="h-7 w-7 rounded-full object-cover max-sm:h-6 max-sm:w-6"
							alt={user.username ?? user.name}
							src={getUserImgSrc(user.image?.id)}
						/>
						<span className="max-sm:text-xs">{user.username ?? user.name}</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					{EVERY_USER_MENU_ITEMS.map(item => (
						<DropdownMenuItem key={item.to} asChild>
							<Link to={`/users/${user.username}/${item.to}`} prefetch="intent">
								<Icon name={item.icon}>{item.label}</Icon>
							</Link>
						</DropdownMenuItem>
					))}
					<hr />
					<DropdownMenuItem asChild>
						<Link
							to={`/users/${user.username}/settings/profile`}
							prefetch="intent"
						>
							<Icon name={'pencil-2'}>Settings</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to={`/users/${user.username}/support`} prefetch="intent">
							<Icon name={'person'}>Support</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={event => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
